import { useNavigate } from "react-router-dom";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useAuth } from "@/context/AuthContext";
import { Heart } from "lucide-react";
import { toast } from "sonner";

/**
 * SaveButton — heart toggle used on cards + detail pages.
 * type: 'jobs' | 'listing' | 'event' | 'need' | 'business'
 * id: itemId (ObjectId string for most, slug for business)
 */
export default function SaveButton({ type, id, size = "sm", className = "", label = true, stopPropagation = true }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const { isSaved, toggle } = useSavedItems(type);
  const saved = isSaved(id);

  const onClick = async (e) => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!user) {
      toast.info("Sign in to save this");
      nav("/login");
      return;
    }
    const r = await toggle(id);
    if (r?.error) toast.error("Could not update your saved list");
    else if (r?.saved) toast.success("Saved");
    else toast("Removed from saved");
  };

  const base = "inline-flex items-center gap-1.5 rounded-full border transition";
  const tone = saved
    ? "bg-[#C05A3D]/10 border-[#C05A3D] text-[#C05A3D]"
    : "bg-white/70 border-[#E2DED0] text-[#5C5C5C] hover:border-[#C05A3D] hover:text-[#C05A3D]";
  const dims = size === "icon" ? "w-9 h-9 justify-center" : "px-3 h-8 text-xs";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${tone} ${dims} ${className}`}
      aria-pressed={saved}
      aria-label={saved ? "Unsave" : "Save"}
      data-testid={`save-btn-${type}-${id}`}
    >
      <Heart className={`w-4 h-4 ${saved ? "fill-current" : ""}`} />
      {size !== "icon" && label && <span>{saved ? "Saved" : "Save"}</span>}
    </button>
  );
}
